import { cva } from 'class-variance-authority';

import { fontBase } from '@/lib/styleUtils';

/**
 * Ink on the primary bar is a literal #FFFFFF, not the `white` token, matching Header.style.tsx:
 * dark mode maps `white` to near-black but leaves `primary` alone (PB-4563).
 */
const ink = 'tw:text-[#FFFFFF]';
const focusOnPrimary =
  'tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-[#FFFFFF] tw:focus-visible:ring-offset-0';
const focusOnPanel =
  'tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-primary tw:focus-visible:ring-inset';

// ---- Bars ------------------------------------------------------------------------------------

export const siteHeaderBarClasses = cva(
  [fontBase, 'tw:box-border tw:w-full tw:bg-primary tw:items-center tw:px-base'],
  {
    variants: {
      layout: {
        /** 64 px primary bar on a `1fr auto 1fr` grid. */
        desktop: 'tw:grid tw:grid-cols-[1fr_auto_1fr] tw:h-16 tw:gap-large tw:md:pl-huge',
        /** 56 px phone bar: menu button, logo or chip, sign in or avatar. */
        mobile: 'tw:flex tw:justify-between tw:h-14 tw:gap-small',
        /** Logo only, at the desktop height, while the navigation loads. */
        skeleton: 'tw:flex tw:h-14 tw:md:h-16 tw:md:pl-huge',
      },
    },
    defaultVariants: { layout: 'desktop' },
  }
);

export const leftClusterClasses =
  'tw:col-start-1 tw:flex tw:items-center tw:gap-colossal tw:min-w-0';
export const centerSlotClasses = 'tw:col-start-2 tw:flex tw:items-center tw:justify-center';
export const rightClusterClasses =
  'tw:col-start-3 tw:justify-self-end tw:flex tw:items-center tw:gap-colossal tw:min-w-0';

export const logoLinkClasses = [
  'tw:flex tw:items-center tw:no-underline tw:cursor-pointer tw:shrink-0',
  focusOnPrimary,
].join(' ');

export const zoneClasses = 'tw:flex tw:items-center tw:gap-large tw:min-w-0';

export const mobileCenterClasses = 'tw:flex tw:flex-1 tw:min-w-0 tw:items-center tw:justify-center';

// ---- Items -----------------------------------------------------------------------------------

export const navItemClasses = cva(
  [
    ink,
    'tw:relative tw:inline-flex tw:items-center tw:gap-micro tw:whitespace-nowrap',
    'tw:text-16 tw:font-regular tw:no-underline tw:bg-transparent tw:border-0 tw:p-0',
    'tw:cursor-pointer tw:transition-opacity tw:duration-150 tw:hover:opacity-80',
    'tw:min-h-[2.75rem] tw:border-b-[3px] tw:border-solid',
    focusOnPrimary,
  ],
  {
    variants: {
      current: {
        // The 3 px accent underline marks the current location (and its top-level ancestor).
        true: 'tw:border-b-[#f9bf3f]',
        false: 'tw:border-b-transparent',
      },
    },
    defaultVariants: { current: false },
  }
);

export const iconClasses = 'tw:shrink-0';
export const uploadIconClasses = 'tw:shrink-0 tw:h-5 tw:w-5 tw:object-contain';

export const tooltipWrapperClasses = 'tw:relative tw:inline-flex';
export const tooltipClasses = [
  'tw:absolute tw:top-full tw:left-1/2 tw:-translate-x-1/2 tw:mt-nano tw:z-40',
  'tw:rounded-4 tw:bg-black tw:px-small tw:py-nano tw:text-12 tw:font-medium',
  'tw:text-[#FFFFFF] tw:whitespace-nowrap tw:pointer-events-none',
].join(' ');

export const signInClasses = [
  ink,
  'tw:inline-flex tw:items-center tw:justify-center tw:min-h-[2.75rem] tw:px-base',
  'tw:rounded-4 tw:border tw:border-solid tw:border-[#FFFFFF] tw:bg-transparent',
  'tw:text-14 tw:font-semibold tw:no-underline tw:hover:bg-[#FFFFFF]/10 tw:whitespace-nowrap',
  focusOnPrimary,
].join(' ');

export const avatarButtonClasses = 'tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer';

export const menuButtonClasses = [
  ink,
  'tw:inline-flex tw:items-center tw:justify-center tw:min-h-[2.75rem] tw:min-w-[2.75rem]',
  'tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer tw:hover:bg-[#FFFFFF]/10 tw:rounded-4',
  focusOnPrimary,
].join(' ');

// ---- Dropdown --------------------------------------------------------------------------------

export const groupWrapperClasses = 'tw:relative tw:inline-flex';

export const dropdownClasses = [
  'tw:absolute tw:top-full tw:left-0 tw:z-40 tw:mt-0 tw:min-w-[14rem]',
  'tw:rounded-8 tw:bg-white tw:text-font tw:shadow-lg tw:py-micro',
  'tw:flex tw:flex-col',
].join(' ');

export const dropdownItemClasses = [
  'tw:relative tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-small tw:text-left',
  'tw:px-base tw:py-small tw:text-14 tw:font-regular tw:text-font tw:no-underline',
  'tw:bg-transparent tw:border-0 tw:cursor-pointer tw:hover:bg-gray-lighter',
  focusOnPanel,
].join(' ');

export const dropdownDividerClasses =
  'tw:my-nano tw:border-0 tw:border-t tw:border-solid tw:border-gray-light';

export const flyoutClasses = [
  'tw:absolute tw:top-0 tw:left-full tw:z-50 tw:min-w-[14rem]',
  'tw:rounded-8 tw:bg-white tw:text-font tw:shadow-lg tw:py-micro tw:flex tw:flex-col',
].join(' ');

export const flyoutHostClasses = 'tw:relative';

// ---- Drawer (PB-9033) ------------------------------------------------------------------------

export const drawerScrimClasses = 'tw:fixed tw:inset-0 tw:z-40 tw:bg-black/50';

export const drawerClasses = [
  fontBase,
  'tw:fixed tw:inset-y-0 tw:left-0 tw:z-50 tw:w-80 tw:max-w-[85vw]',
  'tw:flex tw:flex-col tw:bg-white tw:text-font tw:shadow-lg tw:overflow-y-auto',
].join(' ');

export const drawerHeadClasses =
  'tw:flex tw:items-center tw:justify-between tw:gap-small tw:bg-primary tw:px-base tw:h-14 tw:shrink-0';

export const drawerCloseClasses = menuButtonClasses;

export const drawerBodyClasses = 'tw:flex tw:flex-1 tw:flex-col tw:gap-base tw:px-base tw:py-base';

export const drawerSessionChipClasses = [
  'tw:flex tw:w-full tw:items-center tw:gap-micro tw:text-left tw:text-14 tw:font-semibold',
  'tw:rounded-8 tw:bg-gray-lighter tw:px-base tw:py-small tw:text-font tw:border-0 tw:cursor-pointer',
  focusOnPanel,
].join(' ');

export const drawerZoneClasses = 'tw:flex tw:flex-col';

export const drawerZoneHeadingClasses =
  'tw:text-12 tw:font-semibold tw:uppercase tw:text-gray-dark tw:pb-micro';

export const drawerItemClasses = cva(
  [
    'tw:flex tw:w-full tw:items-center tw:gap-small tw:text-left tw:min-h-[2.75rem]',
    'tw:py-small tw:text-16 tw:font-regular tw:text-font tw:no-underline',
    'tw:bg-transparent tw:border-0 tw:cursor-pointer tw:hover:bg-gray-lighter',
    focusOnPanel,
  ],
  {
    variants: {
      level: {
        1: 'tw:pl-0',
        2: 'tw:pl-base',
        3: 'tw:pl-huge',
      },
    },
    defaultVariants: { level: 1 },
  }
);

export const drawerFootClasses = 'tw:mt-auto tw:flex tw:flex-col tw:pt-base';

export const drawerSignInClasses = [
  'tw:flex tw:w-full tw:items-center tw:justify-center tw:min-h-[2.75rem]',
  'tw:rounded-4 tw:bg-primary tw:px-base tw:text-16 tw:font-semibold tw:no-underline',
  'tw:text-[#FFFFFF]',
  focusOnPanel,
].join(' ');
